/**
 * 동시 실행 수를 제한하는 Promise Pool
 * 병렬 처리 시 리소스 과부하를 방지하기 위해 동시 실행 수를 제한합니다.
 */

/**
 * 개별 항목의 실행 결과
 */
export interface PoolResult<R> {
  status: "fulfilled" | "rejected";
  value?: R;
  reason?: unknown;
  index: number;
}

/**
 * 동시 실행 수를 제한하는 Promise Pool
 * @param items 처리할 항목 배열
 * @param concurrency 동시 실행 수 제한 (1 이상)
 * @param fn 각 항목에 대해 실행할 비동기 함수
 * @returns 모든 항목의 결과 배열 (입력 순서 유지)
 *
 * 에러 격리: 한 항목의 실패가 다른 항목 실행에 영향을 주지 않습니다.
 * 실패한 항목은 PoolResult.status === "rejected"로 표시됩니다.
 */
export async function promisePool<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<PoolResult<R>[]> {
  if (items.length === 0) {
    return [];
  }

  const effectiveConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results: PoolResult<R>[] = new Array(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      try {
        const value = await fn(items[currentIndex], currentIndex);
        results[currentIndex] = { status: "fulfilled", value, index: currentIndex };
      } catch (reason) {
        results[currentIndex] = { status: "rejected", reason, index: currentIndex };
      }
    }
  }

  // worker를 concurrency 수만큼 생성하여 병렬 실행
  const workers = Array.from({ length: effectiveConcurrency }, () => worker());
  await Promise.all(workers);

  return results;
}
